// Pure logic for the Sankirtan coverage map (no React, no Leaflet, no I/O).
//
// Everything here works on the API's own data (GET /tsp/map/coverage): ONE
// point per JOURNEY PLACE (a place of a journey, with its own id), at the
// coordinates SAVED for it — its visit's GPS, else the point chosen on Add
// Journey. Nothing in this file looks a place up by name, and nothing invents
// a position — a place without saved coordinates has no marker.

import { journeyPlacePath } from "./journeyDetailModel";

export const MARKER_STATE = Object.freeze({
  PAST: "past",
  CURRENT: "current",
  UPCOMING: "upcoming",
});

export const ROUTE_STATUS = Object.freeze({
  visited: { label: "Visited", symbol: "●", state: MARKER_STATE.PAST },
  current: { label: "Current location", symbol: "◉", state: MARKER_STATE.CURRENT },
  upcoming: { label: "Upcoming", symbol: "○", state: MARKER_STATE.UPCOMING },
});

// The route card's label for a stop. A stop never visited on a journey that has
// ended (completed / cancelled) will not be visited now: "Not visited", not "Upcoming".
export function routeStopLabel(city, journeyStatus) {
  if (city.status === "upcoming" && (journeyStatus === "COMPLETED" || journeyStatus === "CANCELLED")) return "Not visited";
  return (ROUTE_STATUS[city.status] || ROUTE_STATUS.upcoming).label;
}

// Screen distance (px) under which markers of the SAME state merge into one
// numbered cluster. Markers of different states never merge, so a cluster is
// always "part of one state" and clicking it can open that state.
export const CLUSTER_RADIUS_PX = 34;

export function getVisitMarkerState(item) {
  if (!item) return MARKER_STATE.PAST;
  // An unvisited place of an ended journey: drawn hollow like a planned stop (and greyed as history).
  if (item.marker === "not_visited") return MARKER_STATE.UPCOMING;
  if (item.marker === "current" || item.status === "current" || item.visit_state === "current") {
    return MARKER_STATE.CURRENT;
  }
  if (item.marker === "upcoming" || item.status === "upcoming" || item.visit_state === "not_visited") {
    return MARKER_STATE.UPCOMING;
  }
  return MARKER_STATE.PAST;
}

export function getTspMapVisits(coverage) {
  if (!coverage || !Array.isArray(coverage.points)) return [];
  return coverage.points
    .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
    .map((p) => ({
      ...p,
      markerState: getVisitMarkerState(p),
    }));
}

// "Jaipur → Ajmer → Pushkar → … (+2 more)" and "1 of 3 visited", from the
// map's per-journey route summary (stops in route order). Null without a route.
export function describeRoute(route) {
  if (!route || !route.city_count) return { routeText: null, routeProgress: null };
  const more = route.city_count - route.stops.length;
  return {
    routeText: `${route.stops.join(" → ")}${more > 0 ? ` → … (+${more} more)` : ""}`,
    routeProgress: `${route.visited_city_count} of ${route.city_count} visited`,
  };
}

// The journey's year as the server derived it (from the start date), or null.
const journeyYear = (journey) => (journey ? journey.year ?? (journey.start_date ? Number(String(journey.start_date).slice(0, 4)) : null) : null);

export function getJourneyMapPopupData(point, journey, coverage) {
  if (!point) return null;
  if (point.place_id) return placePopupData(point, coverage);
  // The journey this point is shown for: the route card's journey when the point
  // is one of ITS stops or visits (never another journey's marker labelled with
  // it), else the point's own journey — the same choice the route card makes
  // on click (journeyIdForPoint), so the popup and the card always agree.
  const journeyId = journeyIdForPoint(point, journey ? journey.id : null);
  const listed = (coverage?.journeys || []).find((j) => j.id === journeyId) || null;
  const matchingJourney = listed || (journey && journey.id === journeyId ? journey : null);
  // That journey's own visit here (a spot can hold visits of several journeys / years).
  const ownVisit = (point.visits || []).find((v) => v.journey_id === journeyId) || null;

  const markerState = getVisitMarkerState(point);
  const talukaName = point.city_name || point.label || "Taluka / Town / Municipality";
  const stateName = point.state_name || (coverage?.states || []).find((s) => s.state_id === point.state_id)?.state_name || "";
  const templeName = matchingJourney?.temple_name || "Not specified";
  const journeyName = matchingJourney?.journey_name || matchingJourney?.name || (point.journey_ids?.length ? `Journey #${point.journey_ids[0]}` : "TSP Journey");
  const journeyStatus = matchingJourney?.status || (markerState === MARKER_STATE.CURRENT ? "IN_PROGRESS" : markerState === MARKER_STATE.UPCOMING ? "PLANNED" : "COMPLETED");
  const visitDate = ownVisit?.visit_date || point.last_visit_date || matchingJourney?.start_date || null;
  const leaderName = matchingJourney?.leader?.name || matchingJourney?.team_leader_name || null;
  const areasVisited = ownVisit?.areas_visited || (point.planned_areas || []).filter(Boolean).join(", ") || null;
  const coordinates = Number.isFinite(point.latitude) && Number.isFinite(point.longitude)
    ? `${point.latitude.toFixed(5)}, ${point.longitude.toFixed(5)}`
    : null;
  const { routeText, routeProgress } = describeRoute(matchingJourney?.route);
  // Every journey (and so every year) with a visit or stop at this spot, newest year first.
  const ids = [...new Set([...(point.current_journey_ids || []), ...(point.journey_ids || [])])];
  const journeysHere = ids
    .map((id) => (coverage?.journeys || []).find((j) => j.id === id))
    .filter(Boolean)
    .map((j) => ({ id: j.id, name: j.journey_name, year: journeyYear(j), status: j.status }))
    .sort((a, b) => (b.year || 0) - (a.year || 0) || b.id - a.id);

  return {
    pointKey: point.key,
    journeyId,
    journeyName,
    templeName,
    talukaName,
    districtName: point.district_name || null,
    stateName,
    journeyStatus,
    markerState,
    historical: Boolean(point.historical),
    year: journeyYear(matchingJourney),
    routeText,
    routeProgress,
    journeysHere,
    visitDate,
    leaderName,
    areasVisited,
    coordinates,
    visitCount: point.visit_count || 0,
  };
}

// The journey a marker should open in the route card: `preferredJourneyId`
// (the route card's journey) when the point is one of its own, else a journey
// that is currently at this spot, otherwise the one that visited it most recently.
export function journeyIdForPoint(point, preferredJourneyId = null) {
  if (preferredJourneyId && ((point.current_journey_ids || []).includes(preferredJourneyId) || (point.journey_ids || []).includes(preferredJourneyId))) return preferredJourneyId;
  if (point.current_journey_ids && point.current_journey_ids.length) return point.current_journey_ids[0];
  if (point.visits && point.visits.length) return point.visits[0].journey_id;
  return point.journey_ids && point.journey_ids.length ? point.journey_ids[0] : null;
}

// [[south, west], [north, east]] around the given coordinates, or null.
export function boundsOf(coordinates) {
  const valid = coordinates.filter((c) => Number.isFinite(c.latitude) && Number.isFinite(c.longitude));
  if (valid.length === 0) return null;
  const lats = valid.map((c) => c.latitude);
  const lngs = valid.map((c) => c.longitude);
  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)],
  ];
}

// The points shown on the map for the current view: every point, or the
// selected state's points when a state map is open (`stateIds`: ICC ids).
export function visiblePoints(points, stateIds) {
  return stateIds ? points.filter((point) => stateIds.includes(point.state_id)) : points;
}

// Greedy screen-space clustering. `project(lat, lng) -> { x, y }` is the map's
// own projection at its current zoom (so this re-runs on zoom). A CURRENT
// point is never absorbed into a cluster: the blinking marker must always be
// visible where it really is. Returns items of two kinds:
//   { type: "point", point }
//   { type: "cluster", stateId, points, latitude, longitude }  (mean position)
export function clusterPoints(points, project, radius = CLUSTER_RADIUS_PX) {
  const items = [];
  const clusters = [];
  points.forEach((point) => {
    if (point.marker === "current") {
      items.push({ type: "point", point });
      return;
    }
    const at = project(point.latitude, point.longitude);
    const near = clusters.find((cluster) => cluster.stateId === point.state_id && Math.hypot(cluster.x - at.x, cluster.y - at.y) <= radius);
    if (near) near.points.push(point);
    else clusters.push({ stateId: point.state_id, x: at.x, y: at.y, points: [point] });
  });
  clusters.forEach((cluster) => {
    if (cluster.points.length === 1) {
      items.push({ type: "point", point: cluster.points[0] });
      return;
    }
    const latitude = cluster.points.reduce((sum, p) => sum + p.latitude, 0) / cluster.points.length;
    const longitude = cluster.points.reduce((sum, p) => sum + p.longitude, 0) / cluster.points.length;
    items.push({ type: "cluster", stateId: cluster.stateId, points: cluster.points, latitude, longitude });
  });
  return items;
}

// The state panel's figures — straight from the API's per-state aggregates,
// plus the taluka/town list from that state's points (one row per taluka/town). `selection`
// is { name, stateIds } (see selectionForFeature); a state with no visits at
// all still gets a panel, with zeros from nothing — never invented numbers.
export function stateDetail(coverage, selection) {
  if (!selection) return null;
  const inSelection = (stateId) => selection.stateIds.includes(stateId);
  const states = (coverage.states || []).filter((row) => inSelection(row.state_id));
  const sum = (key) => states.reduce((total, row) => total + (row[key] || 0), 0);
  const cities = new Map();
  (coverage.points || [])
    .filter((point) => inSelection(point.state_id))
    .forEach((point) => {
      const existing = cities.get(point.location_id);
      const visitCount = (existing ? existing.visitCount : 0) + point.visit_count;
      const lastVisit = existing && existing.lastVisit > point.last_visit_date ? existing.lastVisit : point.last_visit_date;
      cities.set(point.location_id, {
        locationId: point.location_id,
        name: point.city_name || point.label,
        label: point.label,
        visitCount,
        lastVisit,
        current: Boolean(existing?.current) || point.marker === "current",
      });
    });
  const journeyIds = new Set((coverage.points || []).filter((point) => inSelection(point.state_id)).flatMap((point) => point.journey_ids));
  return {
    key: selection.key,
    name: selection.name,
    hasVisits: states.length > 0,
    visitedCityCount: sum("visited_city_count"),
    // A journey can cross two ICC states of one selection: count it once.
    journeyCount: states.length === 1 ? states[0].journey_count : new Set(journeyIds).size,
    visitsWithoutCoordinates: sum("visits_without_coordinates"),
    currentCities: states.flatMap((row) => row.current_cities || []),
    cities: [...cities.values()].sort((a, b) => (a.current === b.current ? String(b.lastVisit).localeCompare(String(a.lastVisit)) : a.current ? -1 : 1)),
    journeys: (coverage.journeys || []).filter((journey) => journeyIds.has(journey.id)),
  };
}

// ---- Journey places --------------------------------------------------------
// Every place of every journey is its own entry (`coverage.places`, with or
// without coordinates) and — when it has coordinates — its own marker
// (`coverage.points`). A place is "<Taluka / Town> - <Temple>" (place_label)
// and is identified by its id (place_id), never by its name or position.

const placeYear = (place) => place.year ?? (place.place_date ? Number(String(place.place_date).slice(0, 4)) : null);
const hasPoint = (place) => Number.isFinite(place.latitude) && Number.isFinite(place.longitude);


// The places the Journeys page lists under its filters (the year is applied by
// the server): temple contains; state = the place's own state; search = the
// place, its district / state, its temple or its journey's name; statuses =
// the journey statuses of the Status filter (e.g. "COMPLETED"), empty = all;
// from/to = date range boundaries in YYYY-MM-DD.
export function filterMapPlaces(coverage, { q = "", temple = "", stateId = "", statuses = "", from = "", to = "" } = {}) {
  const places = coverage?.places || [];
  const needle = String(q).trim().toLowerCase();
  const templeNeedle = String(temple).trim().toLowerCase();
  const state = Number(stateId) || null;
  const statusSet = statuses ? new Set(String(statuses).split(",")) : null;
  const fromDate = from ? (from instanceof Date ? from.toISOString().slice(0, 10) : String(from).slice(0, 10)) : "";
  const toDate = to ? (to instanceof Date ? to.toISOString().slice(0, 10) : String(to).slice(0, 10)) : "";

  if (!needle && !templeNeedle && !state && !statusSet && !fromDate && !toDate) return places;
  const journeyName = new Map((coverage?.journeys || []).map((journey) => [journey.id, journey.journey_name]));
  const journeyStartDate = new Map((coverage?.journeys || []).map((journey) => [journey.id, journey.start_date]));
  const has = (value, text) => String(value || "").toLowerCase().includes(text);

  return places.filter((place) => {
    if (statusSet && !statusSet.has(place.journey_status)) return false;
    if (templeNeedle && !has(place.temple_name, templeNeedle)) return false;
    if (state && place.state_id !== state) return false;

    if (fromDate || toDate) {
      const rawDate = place.place_date || place.last_visit_date || place.date || place.year_date || journeyStartDate.get(place.journey_id) || "";
      const placeDate = rawDate instanceof Date ? rawDate.toISOString().slice(0, 10) : String(rawDate).slice(0, 10);
      if (!placeDate) return false;
      if (fromDate && placeDate < fromDate) return false;
      if (toDate && placeDate > toDate) return false;
    }

    if (!needle) return true;
    return [place.place_label, place.city_name, place.district_name, place.state_name, place.temple_name, journeyName.get(place.journey_id)].some((value) => has(value, needle));
  });
}

// The places grouped by their year (the place's date, else its journey's start
// year — server-derived), newest year first; within a year the API's order
// (newest place date first). Places without any year come last, as `year: null`.
export function groupPlacesByYear(places) {
  const groups = new Map();
  (places || []).forEach((place) => {
    const year = placeYear(place);
    if (!groups.has(year)) groups.set(year, []);
    groups.get(year).push(place);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : b - a))
    .map(([year, rows]) => ({ year, places: rows }));
}

const MARKER_RANK = { current: 3, visited: 2, upcoming: 1, not_visited: 0 };
const POSITION_DIGITS = 4; // ≈ 11 m, as the server rounds a visit spot

// The map's markers for these places: one per place with coordinates. Places
// at the very same spot (the same town on several journeys, all from one
// temple) would hide one another, so they share ONE marker that lists every
// place there (`places`); its look follows the liveliest of them (current >
// visited > planned). A lone place is its own marker (`places: [itself]`).
export function stackPlacePoints(places) {
  const stacks = new Map();
  (places || []).filter(hasPoint).forEach((place) => {
    const at = `${place.latitude.toFixed(POSITION_DIGITS)}:${place.longitude.toFixed(POSITION_DIGITS)}`;
    if (!stacks.has(at)) stacks.set(at, []);
    stacks.get(at).push(place);
  });
  return [...stacks.values()].map((members) => {
    if (members.length === 1) return { ...members[0], places: members };
    const lead = [...members].sort((a, b) => (MARKER_RANK[b.marker] || 0) - (MARKER_RANK[a.marker] || 0))[0];
    return {
      ...lead,
      key: `stack:${members.map((member) => member.place_id).sort((a, b) => a - b).join("-")}`,
      places: members,
      journey_ids: [...new Set(members.flatMap((member) => member.journey_ids || []))],
      current_journey_ids: [...new Set(members.flatMap((member) => member.current_journey_ids || []))],
      visits: members.flatMap((member) => member.visits || []),
      visit_count: members.reduce((sum, member) => sum + (member.visit_count || 0), 0),
      historical: members.every((member) => member.historical),
    };
  });
}

// The marker that shows a place (its own, or the shared one at its spot).
export function pointKeyForPlace(points, placeId) {
  const point = (points || []).find((candidate) => (candidate.places || [candidate]).some((member) => member.place_id === placeId));
  return point ? point.key : null;
}

// A place marker's popup: THAT place — its name, temple, date, status, visit —
// never its journey's other places. A shared marker also lists every place at
// the spot, each opening its own journey place.
function placePopupData(point, coverage) {
  const journey = (coverage?.journeys || []).find((row) => row.id === point.journey_id) || null;
  const markerState = getVisitMarkerState(point);
  const ownVisit = (point.visits || []).find((visit) => visit.journey_id === point.journey_id) || null;
  const members = point.places || [point];
  return {
    pointKey: point.key,
    journeyId: point.journey_id,
    placeId: point.place_id,
    path: journeyPlacePath(point.journey_id, point.place_id),
    journeyName: point.place_label,
    templeName: point.temple_name || "Not specified",
    talukaName: point.city_name || point.label || "Taluka / Town / Municipality",
    districtName: point.district_name || null,
    stateName: point.state_name || (coverage?.states || []).find((state) => state.state_id === point.state_id)?.state_name || "",
    journeyStatus: point.journey_status || journey?.status || null,
    markerState,
    notVisited: point.marker === "not_visited",
    historical: Boolean(point.historical),
    year: placeYear(point),
    dateLabel: "Date",
    visitDate: point.place_date || null,
    leaderName: journey?.leader?.name || null,
    areasVisited: ownVisit?.areas_visited || (point.planned_areas || []).filter(Boolean).join(", ") || null,
    coordinates: hasPoint(point) ? `${point.latitude.toFixed(5)}, ${point.longitude.toFixed(5)}` : null,
    routeText: null,
    routeProgress: null,
    journeysHere: [],
    placesHere:
      members.length > 1
        ? members.map((member) => ({ placeId: member.place_id, journeyId: member.journey_id, label: member.place_label, date: member.place_date || null, path: journeyPlacePath(member.journey_id, member.place_id) }))
        : [],
    visitCount: point.visit_count || 0,
  };
}

// ---- State boundaries (Survey of India polygons) ---------------------------
// A polygon names the ICC state(s) it belongs to (`icc_names`); the API names
// states by ICC id + name. Matching is by normalised name only — the polygon is
// an outline to click, never a position source.
export function normalizeStateName(name) {
  return String(name || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z]/g, "");
}

// The selection a clicked polygon (or a listed state) stands for:
// { key, name, stateIds: ICC ids, lgdCodes: polygons to highlight }.
export function selectionForFeature(feature, coverage, boundaries) {
  const iccNames = (feature.properties.icc_names || [feature.properties.name]).map(normalizeStateName);
  const states = (coverage?.states || []).filter((state) => iccNames.includes(normalizeStateName(state.state_name)));
  const lgdCodes = (boundaries?.features || [])
    .filter((other) => (other.properties.icc_names || []).some((name) => iccNames.includes(normalizeStateName(name))))
    .map((other) => other.properties.lgd_code);
  return {
    key: `lgd:${feature.properties.lgd_code}`,
    name: states.length === 1 ? states[0].state_name : feature.properties.name,
    stateIds: states.map((state) => state.state_id),
    lgdCodes: lgdCodes.length ? lgdCodes : [feature.properties.lgd_code],
  };
}

export function selectionForState(stateId, coverage, boundaries) {
  const state = (coverage?.states || []).find((row) => row.state_id === stateId);
  if (!state) return null;
  const name = normalizeStateName(state.state_name);
  const lgdCodes = (boundaries?.features || []).filter((f) => (f.properties.icc_names || []).some((n) => normalizeStateName(n) === name)).map((f) => f.properties.lgd_code);
  return { key: `state:${stateId}`, name: state.state_name, stateIds: [stateId], lgdCodes };
}

// [[south, west], [north, east]] of the given polygons, or null.
export function featuresBounds(boundaries, lgdCodes) {
  const coordinates = [];
  (boundaries?.features || [])
    .filter((feature) => lgdCodes.includes(feature.properties.lgd_code))
    .forEach((feature) => {
      const polygons = feature.geometry.type === "Polygon" ? [feature.geometry.coordinates] : feature.geometry.coordinates;
      polygons.forEach((polygon) => polygon[0].forEach(([longitude, latitude]) => coordinates.push({ latitude, longitude })));
    });
  return boundsOf(coordinates);
}

// Every state name shown in the route card header, in route order.
export function routeStateNames(journey) {
  return (journey?.states || []).map((state) => state.name).filter(Boolean);
}

// ---------- "India only" mask ----------
function ringArea(ring) {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) sum += (ring[j][0] - ring[i][0]) * (ring[j][1] + ring[i][1]);
  return sum;
}

function pointInRing([x, y], ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function outsideIndiaMask(nationalBoundary) {
  const rings = (nationalBoundary?.features || [])
    .flatMap((feature) => {
      const geometry = feature.geometry || {};
      if (geometry.type === "MultiLineString") return geometry.coordinates;
      if (geometry.type === "LineString") return [geometry.coordinates];
      if (geometry.type === "Polygon") return [geometry.coordinates[0]];
      if (geometry.type === "MultiPolygon") return geometry.coordinates.map((polygon) => polygon[0]);
      return [];
    })
    .filter((ring) => ring.length >= 4 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1] && ringArea(ring) !== 0);
  if (rings.length === 0) return null;

  const mainland = rings.reduce((largest, ring) => (Math.abs(ringArea(ring)) > Math.abs(ringArea(largest)) ? ring : largest));
  const holes = rings.filter((ring) => ring === mainland || !pointInRing(ring[0], mainland));
  const world = [
    [-180, -85],
    [180, -85],
    [180, 85],
    [-180, 85],
    [-180, -85],
  ];
  return {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: [world, ...holes.map((ring) => (ringArea(ring) > 0 ? [...ring].reverse() : ring))] },
  };
}
